import {
  addDoc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import {
  ensureLeagueDataBoundary,
  leagueCollection,
  leagueCollections,
  leagueDataRef,
  leagueDocument,
} from "./leagueRepository";

export const DEFAULT_SEASON = {
  id: "season-01",
  name: "Season 01",
  divisionName: "Gold League",
  status: "draft",
  currentRound: 0,
  totalRounds: 12,
  winPoints: 3,
  drawPoints: 1,
  lossPoints: 0,
  promotionPlaces: 2,
  relegationPlaces: 2,
  startDate: "",
  endDate: "",
  daysBetweenRounds: 7,
  homeAndAway: true,
  promotionEnabled: true,
  relegationEnabled: true,
  operationalBonusMax: 3,
  operationalWeights: { neo: 0.5, morpheus: 0.5 },
  operationalThresholds: { bronze: 70, silver: 80, gold: 90 },
};

export function leagueRootRef(orgId) {
  return leagueDataRef(orgId);
}

export function seasonRef(orgId, seasonId = DEFAULT_SEASON.id) {
  return leagueDocument(orgId, leagueCollections.seasons, seasonId);
}

export function clubsRef(orgId) {
  return leagueCollection(orgId, leagueCollections.clubs);
}

export function subscribeSeason(orgId, seasonId, onValue, onError) {
  return onSnapshot(seasonRef(orgId, seasonId), (snap) => {
    onValue(snap.exists() ? { id: snap.id, ...snap.data() } : null);
  }, onError);
}

export function subscribeClubs(orgId, onValue, onError) {
  const clubsQuery = query(clubsRef(orgId), orderBy("name", "asc"));
  return onSnapshot(clubsQuery, (snap) => {
    onValue(snap.docs.map((clubDoc) => ({ id: clubDoc.id, ...clubDoc.data() })));
  }, onError);
}

export async function ensureLeagueSeason(orgId, seasonId = DEFAULT_SEASON.id, seed = DEFAULT_SEASON) {
  await ensureLeagueDataBoundary(orgId);
  const ref = seasonRef(orgId, seasonId);
  const snap = await getDoc(ref);
  if (snap.exists()) return { id: snap.id, ...snap.data() };
  const payload = { ...DEFAULT_SEASON, ...(seed || {}), id: seasonId, createdAt: serverTimestamp(), updatedAt: serverTimestamp() };
  await setDoc(ref, payload);
  await setDoc(leagueRootRef(orgId), { activeSeasonId: seasonId, updatedAt: serverTimestamp() }, { merge: true });
  return payload;
}

export async function saveSeason(orgId, season) {
  const seasonId = String(season?.id || DEFAULT_SEASON.id);
  const payload = {
    ...DEFAULT_SEASON,
    ...season,
    id: seasonId,
    currentRound: Number(season?.currentRound || 0),
    totalRounds: Number(season?.totalRounds || 12),
    winPoints: Number(season?.winPoints ?? 3),
    drawPoints: Number(season?.drawPoints ?? 1),
    lossPoints: Number(season?.lossPoints ?? 0),
    promotionPlaces: Number(season?.promotionPlaces ?? 2),
    relegationPlaces: Number(season?.relegationPlaces ?? 2),
    daysBetweenRounds: Number(season?.daysBetweenRounds || 7),
    endDate: season?.endDate || "",
    homeAndAway: season?.homeAndAway !== false,
    promotionEnabled: season?.promotionEnabled !== false,
    relegationEnabled: season?.relegationEnabled !== false,
    operationalBonusMax: Number(season?.operationalBonusMax ?? 3),
    operationalWeights: {
      neo: Number(season?.operationalWeights?.neo ?? 0.5),
      morpheus: Number(season?.operationalWeights?.morpheus ?? 0.5),
    },
    operationalThresholds: {
      bronze: Number(season?.operationalThresholds?.bronze ?? 70),
      silver: Number(season?.operationalThresholds?.silver ?? 80),
      gold: Number(season?.operationalThresholds?.gold ?? 90),
    },
    updatedAt: serverTimestamp(),
  };
  await ensureLeagueDataBoundary(orgId);
  await setDoc(seasonRef(orgId, seasonId), payload, { merge: true });
  await setDoc(leagueRootRef(orgId), { activeSeasonId: seasonId, updatedAt: serverTimestamp() }, { merge: true });
  return payload;
}

export async function addClub(orgId, club) {
  const payload = {
    name: String(club?.name || "").trim(),
    siteId: String(club?.siteId || "").trim(),
    division: String(club?.division || "gold").trim().toLowerCase(),
    badge: String(club?.badge || club?.name?.charAt(0) || "M").trim().slice(0, 2).toUpperCase(),
    manager: String(club?.manager || "").trim(),
    played: Number(club?.played || 0),
    won: Number(club?.won || 0),
    drawn: Number(club?.drawn || 0),
    lost: Number(club?.lost || 0),
    operationalPoints: Number(club?.operationalPoints || 0),
    bonusPoints: Number(club?.bonusPoints || 0),
    form: Array.isArray(club?.form) ? club.form.slice(0, 5) : [],
    active: club?.active !== false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  if (!payload.name) throw new Error("Club name is required.");
  return addDoc(clubsRef(orgId), payload);
}

export async function updateClub(orgId, clubId, changes) {
  return updateDoc(leagueDocument(orgId, leagueCollections.clubs, clubId), {
    ...changes,
    updatedAt: serverTimestamp(),
  });
}

export async function finalizeExpiredSeason(orgId, season, clubs = [], competitions = []) {
  if (!orgId || !season?.id || season.status !== "live" || !season.endDate) return false;
  const end = new Date(`${season.endDate}T23:59:59`);
  if (Number.isNaN(end.getTime()) || end.getTime() > Date.now()) return false;
  const { writeBatch } = await import("firebase/firestore");
  const { buildStandings } = await import("./pointsEngine");
  const standings = buildStandings(clubs, season, competitions);
  const gold = standings.filter((site) => String(site.division || "gold").toLowerCase() === "gold");
  const silver = standings.filter((site) => String(site.division || "gold").toLowerCase() === "silver");
  const promoteCount = Math.max(0, Number(season.promotionPlaces || 1));
  const relegateCount = Math.max(0, Number(season.relegationPlaces || 1));
  const promoted = silver.slice(0, promoteCount);
  const relegated = relegateCount ? gold.slice(-relegateCount) : [];
  const batch = writeBatch((await import("../../firebaseConfig")).db);
  promoted.forEach((site) => batch.update(leagueDocument(orgId, leagueCollections.clubs, site.id), { division: "gold", operationalPoints: 0, bonusPoints: 0, played: 0, won: 0, drawn: 0, lost: 0, updatedAt: serverTimestamp() }));
  relegated.forEach((site) => batch.update(leagueDocument(orgId, leagueCollections.clubs, site.id), { division: "silver", operationalPoints: 0, bonusPoints: 0, played: 0, won: 0, drawn: 0, lost: 0, updatedAt: serverTimestamp() }));
  const compact = (items) => items.map((site, index) => ({ id: site.id, name: site.name, badge: site.badge || "", manager: site.manager || "", division: site.division || "gold", position: index + 1, played: site.played || 0, won: site.won || 0, points: site.points || 0 }));
  batch.set(seasonRef(orgId, season.id), { status: "archived", finalStandings: compact(standings), goldFinalStandings: compact(gold), silverFinalStandings: compact(silver), goldChampion: gold[0] ? { id: gold[0].id, name: gold[0].name, badge: gold[0].badge || "", manager: gold[0].manager || "", points: gold[0].points || 0, won: gold[0].won || 0 } : null, goldRunnerUp: gold[1] ? { id: gold[1].id, name: gold[1].name, badge: gold[1].badge || "", manager: gold[1].manager || "", points: gold[1].points || 0, won: gold[1].won || 0 } : null, silverChampion: silver[0] ? { id: silver[0].id, name: silver[0].name, badge: silver[0].badge || "", manager: silver[0].manager || "", points: silver[0].points || 0, won: silver[0].won || 0 } : null, promoted: promoted.map((x) => ({ id: x.id, name: x.name })), relegated: relegated.map((x) => ({ id: x.id, name: x.name })), archivedAt: serverTimestamp(), updatedAt: serverTimestamp() }, { merge: true });
  const nextId = `season-${Date.now()}`;
  batch.set(seasonRef(orgId, nextId), { ...DEFAULT_SEASON, id: nextId, name: `${season.name || "Season"} Next`, status: "draft", promotionPlaces: promoteCount, relegationPlaces: relegateCount, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
  batch.set(leagueRootRef(orgId), { activeSeasonId: nextId, updatedAt: serverTimestamp() }, { merge: true });
  await batch.commit();
  return true;
}
